/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import mixpanel from "mixpanel-browser";

import { getRandomProfileEmoji, OpenInNewWindow } from "@/Helpers/helper";
import { cn } from "@/lib/utils";

import {
  CheckCircleIcon,
  ArrowRightIcon,
  CheckCircle2,
  Check,
  Info,
  MessageCircle,
  ThumbsUp,
  Repeat2,
  TrendingUp,
  Eye,
  InfoIcon,
  Edit2,
  LucideIcon,
} from "lucide-react";
import { GrAdd } from "react-icons/gr";
import { useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdMailOutline } from "react-icons/md";
import { FiPhoneCall } from "react-icons/fi";

import { Button } from "@/shadcnComponents/ui/button";
import { Label } from "@/shadcnComponents/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/shadcnComponents/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcnComponents/ui/alert-dialog";

import { CreateNewCampaign } from "@/Components/Brand/Sidebar/Sidebar";
import { useAuth } from "@/Providers/Auth";
import { useNewCampaign } from "../CustomHooks/NewCampaignHooks";

export const NoItemToDisplayMessage = () => {
  const navigate = useNavigate();
  return (
    <>
      <div className="h-[50vh] flex items-center justify-center ">
        <div className="flex flex-col gap-2 items-center ">
          <p className="text-[color:var(--Natural-500,#9E9E9E)] text-sm font-semibold leading-5 italic">
            No items to display
          </p>
          <CreateNewCampaign />
          <Button
            onClick={() =>
              navigate("/Brand/DashBoard/AllCampaign/Draft/sample")
            }
            size="sm"
            variant="destructive"
            className="transition w-full justify-start group-data-[collapsible=icon] ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300"
          >
            Create Sample Campaign
          </Button>
        </div>
      </div>
    </>
  );
};

export const TableRow = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <span
      className={cn(
        " flex flex-wrap items-center justify-start p-1  overflow-hidden text-[color:var(--Natural-800,#424242)] text-ellipsis  text-sm not-italic font-normal leading-5 ",
        className
      )}
    >
      {children}
    </span>
  );
};

export const SuccessPage = ({
  SuccessStepperList,
  navigateTo,
  navigatePrev,
}: {
  SuccessStepperList: {
    completed: boolean;
    title: string;
  }[];
  navigateTo: () => void;
  navigatePrev?: () => void;
}) => {
  return (
    <>
      <div className="flex-1 py-5 mb-5 h-auto space-y-4">
        <div className="flex flex-col items-center justify-center mt-[120px]">
          <div className="flex flex-col gap-1 w-[434px] ">
            <div className="w-full flex justify-center">
              <CheckCircle2 size={70} color="#4ECB71" />
            </div>
            <h1 className="text-[color:var(--Natural-900,#212121)] text-center text-2xl not-italic font-bold leading-[normal]">
              Congratulations! <br /> Campaign Activated
            </h1>
            <h3 className="text-[color:var(--Natural-900,#212121)] text-center text-sm not-italic font-normal leading-[normal]">
              Selected influencers have been notified about your campaign <br />{" "}
              Be patient as they accept it & submit drafts
            </h3>
          </div>
          <ul className="relative flex flex-col md:flex-row gap-2 mt-[75px] justify-center">
            {SuccessStepperList?.map((item) => (
              <li className="md:shrink md:basis-0 flex-1 group flex gap-x-2 md:block">
                <div className="min-w-7 min-h-7 flex flex-col items-center md:w-full md:inline-flex md:flex-wrap md:flex-row text-xs align-middle">
                  <span className="size-7 flex justify-center animate-pulse items-center flex-shrink-0 bg-gray-100 font-medium border text-gray-800 rounded-full dark:bg-neutral-700 dark:text-white">
                    {item?.completed ? <CheckCircle2 /> : " "}
                  </span>
                  <div className="mt-2 w-px h-full md:mt-0 md:ms-2 md:w-full md:h-px md:flex-1 bg-gray-200 group-last:hidden dark:bg-neutral-700"></div>
                </div>
                <div className="grow md:grow-0 md:mt-3 pb-5">
                  <span
                    className={`block text-sm text-gray-800 dark:text-white ${
                      item?.completed ? "font-bold" : "font-medium "
                    } `}
                  >
                    {item?.title}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <div className="w-full flex mt-[70px] items-center justify-center gap-4 ">
            {navigatePrev && (
              <Button onClick={navigatePrev} variant="outline">
                Set Custom Live Dates
              </Button>
            )}
            <Button
              onClick={() => {
                mixpanel.track(
                  "Brand Draft Campaign - Success Page: Go To DashBoard - clicked"
                );
                navigateTo();
              }}
              variant="destructive"
              className="flex gap-[10px] items-center "
            >
              {" "}
              <span>Go to Dashboard</span> <ArrowRightIcon size={16} />{" "}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export const StepperCard = ({
  List,
  handleClick,
  fillCondition,
  animateCondition,
  completeCondition,
  isNavigation = true,
}: {
  List: { title: string; icon?: LucideIcon; description?: string }[];
  handleClick: (index: number) => void;
  fillCondition: (index: number) => boolean;
  animateCondition: (index: number) => boolean;
  completeCondition: (index: number) => boolean;
  isNavigation?: boolean;
}) => {
  return (
    <>
      <div className="flex mt-12">
        <ol className="relative font-inter  border-gray-200 dark:border-gray-700 dark:text-gray-400">
          {List?.map((item, index) => (
            <li
              key={`SideBar_stepper${index}`}
              onClick={() => {
                isNavigation ? (
                  <>
                    {mixpanel.track(
                      `Brand Campaign Sidebar: ${item?.title} - clicked`
                    )}
                    {handleClick(index)}{" "}
                  </>
                ) : (
                  ""
                );
              }}
              className={`gap-3 flex mb-4 rounded-md items-center cursor-pointer py-[6px] px-3 hover:bg-[#4b4b4b] hover:text-[#FAFAFA] ${
                animateCondition(index)
                  ? "text-white bg-[#4b4b4b]"
                  : "text-[#212121]"
              } `}
            >
              <span
                className={`flex items-center justify-center w-5 h-5 ${
                  fillCondition(index)
                    ? animateCondition(index)
                      ? "bg-white"
                      : "bg-black"
                    : "bg-gray-100"
                }  rounded-full  ${
                  animateCondition(index) ? "" : ""
                }  ring-1 ring-black dark:ring-gray-900 dark:bg-green-900`}
              >
                {completeCondition(index) ? (
                  <Check
                    size={18}
                    color={animateCondition(index) ? "black" : "white"}
                  />
                ) : (
                  ""
                )}
              </span>
              <h3 className=" text-sm not-italic font-medium leading-6">
                {item?.title}
              </h3>
              {/* <p className="text-xs">{item?.description}</p> */}
            </li>
          ))}
        </ol>
      </div>
    </>
  );
};

export const CampaignSidebar = ({
  children,
  navigateTo,
  Name,
  handleSetName,
  BottomButtons,
}: {
  children?: React.ReactNode;
  navigateTo?: string;
  Name?: string;
  handleSetName?: (string) => void;
  BottomButtons?: React.ReactNode[];
}) => {
  const navigate = useNavigate();
  return (
    <>
      <div
        className="flex flex-col w-[245px] min-w-[245px] fixed top-0  items-center  p-5 box-border gap-5 font-inter h-full z-10"
        style={{ boxShadow: "4px 0px 8px 0px rgba(33, 33, 33, 0.08)" }}
      >
        {navigateTo && (
          <div className="w-full">
            <Button
              className="gap-2 text-[#AFAFAF] text-sm not-italic font-medium leading-6"
              onClick={() => {
                mixpanel.track("Campaign Sidebar: Back To Dashboard - clicked");
                navigate(navigateTo);
              }}
              variant="link"
            >
              <IoMdArrowRoundBack />
              Back to Dashboard
            </Button>
          </div>
        )}
        {
          <div className="w-full flex flex-col items-start gap-1.5 self-stretch rounded border px-3 py-2 border-solid border-[#D6D6D6]">
            <label className="text-[#3D3B3B] text-sm not-italic font-bold leading-5">
              Campaign Name
            </label>
            <div className="flex gap-[1px] items-center w-full ">
              <input
                type="text"
                value={Name}
                disabled={!handleSetName}
                onChange={(e) => handleSetName(e.target.value)} // assuming `setName` is the setter function for `Name`
                className="text-[#615C5C] text-xs not-italic font-normal leading-[18px] bg-transparent border-none focus:outline-none w-[90%] "
              />
              {handleSetName && <Edit2 size={12} color="#615C5C" />}
            </div>
          </div>
        }
        <section
          style={{ scrollbarWidth: "none" }}
          className="flex-1 overflow-x-scroll"
        >
          {children}
        </section>
        <section className="w-full">
          <div className="flex flex-col items-start gap-2 w-full">
            {BottomButtons?.map((item, index) => (
              <div key={index} className="w-full">
                {item}
              </div>
            ))}
          </div>
        </section>
        <section>
          <div className="flex flex-col items-center gap-2 border rounded-[5px] border-solid border-[#EEE] p-2">
            <div className="flex  items-center gap-2">
              <img
                src={
                  "https://anchors-assets.s3.ap-south-1.amazonaws.com/aesha_anchors.png"
                }
                alt="logo"
                className="w-10 h-10 rounded-full"
              />

              <section className="flex flex-col gap-1">
                <span className="flex gap-1 items-center text-[16px] mt-[-10px] font-bold text-[#424242]">
                  {"Need help?"}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={() => {
                            mixpanel.track(
                              "Campaign Sidebar: Need Help Phone - clicked"
                            );
                            window.open("tel:+918799710137");
                          }}
                          className="p-0"
                          variant="ghost"
                        >
                          <FiPhoneCall size={18} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>+918799710137</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={() => {
                            mixpanel.track(
                              "Campaign Sidebar: Need Help Email - clicked"
                            );
                            window.open("mailto:collab@anchors.in");
                          }}
                          className="p-0"
                          variant="ghost"
                        >
                          <MdMailOutline size={18} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>collab@anchors.in</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
                <p className="text-[#121212] text-xs not-italic mt-[-10px] font-medium leading-[normal]">
                  {"I'm Aesha, here for you!"}
                </p>
              </section>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export const ToolTipInfo = ({
  Content,
  size,
}: {
  Content: React.ReactNode;
  size?: number;
}) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="cursor-pointer" size={size ?? 17} />
        </TooltipTrigger>
        <TooltipContent className="max-w-sm text-left">
          {Content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

//fields
export const CampaignFormField = ({
  labelHead,
  labelDesc,
  Children,
  mandatory,
  className,
  labelToolTipContent,
  labelHeadRightIcon,
}: {
  labelHead?: string;
  labelDesc?: string;
  Children?: React.ReactNode;
  mandatory?: boolean;
  className?: string;
  labelToolTipContent?: string | React.ReactNode;
  labelHeadRightIcon?: React.ReactNode;
}) => {
  return (
    <>
      <div className={cn("w-full space-y-2", className)}>
        {labelHead && (
          <div className="flex gap-1 items-center">
            <Label htmlFor="purpose">
              {labelHead}{" "}
              {mandatory && <span className="text-red text-[#DC2627] ">*</span>}
            </Label>
            {labelHeadRightIcon}
            {labelToolTipContent && (
              <ToolTipInfo size={14} Content={labelToolTipContent} />
            )}
          </div>
        )}
        {labelDesc && (
          <p className=" text-gray-500 text-xs not-italic font-normal leading-5 ">
            {labelDesc}
          </p>
        )}
        {Children}
      </div>
    </>
  );
};

export const CampaignReviewFiled = ({
  heading,
  value,
  className,
}: {
  heading: string;
  value: string | React.ReactNode;
  className?: string;
}) => {
  return (
    <>
      <div className="ql-snow">
        <div
          data-gram="false"
          className={cn(
            "flex items-start font-inter flex-col gap-[5px]",
            className
          )}
        >
          <span className="text-[color:var(--Natural-500,#9E9E9E)] text-center text-sm not-italic font-semibold leading-5 p-1 ">
            {heading}
          </span>
          <span className="text-[#121212] text-sm not-italic font-normal  leading-5 p-1 break-words ">
            {value}
          </span>
        </div>
      </div>
    </>
  );
};

export const CampaignReviewHeader = ({
  Icon,
  Heading,
}: {
  Icon?: React.ReactNode;
  Heading: string;
}) => {
  return (
    <>
      <span className="flex gap-[5px] items-center justify-center ">
        {Icon}
        <span className="text-[#121212] font-[Inter] text-[16px] not-italic font-semibold leading-[normal]">
          {Heading}
        </span>
      </span>
    </>
  );
};

export const CheckOutCardField = ({
  Heading,
  Subheading,
  Value,
  bg,
  textColor = "slate-500",
  textSize = "sm",
}: {
  Heading: string | React.ReactNode;
  Subheading?: string | React.ReactNode;
  Value: any;
  bg?: string;
  textColor?: string;
  textSize?: string;
}) => {
  return (
    <div
      className={cn(
        `flex items-center  justify-between p-[10px] rounded-[6px]`,
        bg,
        `text-${textColor}`,
        `text-${textSize}`
      )}
    >
      <div className="flex flex-col gap-2 items-start justify-center ">
        <span className=" not-italic font-semibold leading-[normal]">
          {Heading}
        </span>
        {Subheading && (
          <span className=" text-xs not-italic font-normal leading-[normal]">
            {Subheading}
          </span>
        )}
      </div>
      <span className=" text-right not-italic font-normal leading-6;">
        {Value}
      </span>
    </div>
  );
};

//Buttons
interface SelectInfluButtonProps {
  isSelected: boolean;
  onClick: () => void;
  index: number;
}

export const SelectInfluButton: React.FC<SelectInfluButtonProps> = ({
  isSelected,
  onClick,
  index,
}) => {
  return (
    <Button
      variant={isSelected ? "secondary" : "destructive"}
      onClick={onClick}
      id={`search_creator_${index}`}
      className={`gap-2 ${
        isSelected ? "text-black" : "text-white"
      } tracking-wider`}
    >
      {" "}
      {isSelected ? <CheckCircleIcon size={16} /> : <GrAdd />}{" "}
      <span>{isSelected ? "Selected" : "Select Influencer"}</span>
    </Button>
  );
};

//cards
interface PerformanceStatsCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  iconBgColor?: string;
  valueColor?: string;
}

export const PerformanceStatsCard: React.FC<PerformanceStatsCardProps> = ({
  icon,
  value,
  label,
  iconBgColor,
  valueColor,
}) => {
  return (
    <div className="flex gap-7 items-center">
      <div className={cn("bg-[#FFE8E8] rounded-full p-4", iconBgColor)}>
        <div className="flex items-center justify-center size-[30px] text-[#DC2627]">
          {icon}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <span className={cn("text-2xl font-bold", valueColor)}>{value}</span>
        <span className="text-[#424242] text-xs">{label}</span>
      </div>
    </div>
  );
};

interface InfluPostCardProps {
  profile: string;
  name: string;
  postDescription: string;
  likes?: number;
  comments?: number;
  reposts?: number;
  postLink?: string;
  tagline?: string;
  impressions?: number;
  engagementRate?: number;
}

export const InfluPostCard: React.FC<InfluPostCardProps> = ({
  profile,
  name,
  postDescription,
  likes,
  comments,
  reposts,
  postLink,
  tagline,
  impressions,
  engagementRate,
}) => {
  return (
    <>
      <div className="w-[275px] shrink-0 p-4 flex flex-col border border-solid border-[#DBDBDB]  gap-[20px] rounded-[10px] justify-between [box-shadow:0px_4px_4px_0px_rgba(0,_0,_0,_0.25)]">
        <div className="flex flex-col gap-[20px]">
          <div className="w-full items-center justify-start flex gap-2 ">
            <div className="overflow-hidden size-[50px] shrink-0 rounded-full  ">
              <img
                className="size-[50px] shrink-0 cover "
                src={profile}
                alt=""
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onError={(e: any) => {
                  const randomProfileEmoji = getRandomProfileEmoji();
                  e.target.onerror = null;
                  e.target.src = randomProfileEmoji;
                }}
              />
            </div>
            <div className="w-full flex flex-col gap-1">
              <span className=" text-base font-semibold leading-[14.52px] text-left">
                {name}
              </span>
              <span className="text-xs text-[#121212] font-inter font-light text-left line-clamp-1 ">
                {tagline}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap flex-col gap-1 ">
            <div className="text-[#585757] font-[Inter] text-sm not-italic font-normal leading-[22px] break-all line-clamp-[9]">
              {postDescription}
            </div>
            <span
              onClick={() => OpenInNewWindow({ link: postLink })}
              className=" cursor-pointer text-xs font-bold leading-[14.52px] text-left text-[#666666] line-clamp-4"
            >
              view more
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <div className=" flex gap-[20px]">
            {[
              {
                icon: <ThumbsUp size={20} color="#121212" />,
                key: "Avg. Engagement",
                value: `${likes ?? "NA"}`,
              },
              {
                icon: <MessageCircle size={20} color="#121212" />,
                key: "Avg. Likes",
                value: `${comments ?? "NA"}`,
              },
              {
                icon: <Repeat2 size={20} color="#121212" />,
                key: "Avg. Comments",
                value: `${reposts ?? "NA"}`,
              },
            ]?.map((item, i) => {
              if (item?.value === "NA" || parseInt(item?.value) <= 0)
                return null;
              return (
                <div
                  key={i}
                  className="flex gap-1 items-center  @apply text-sm font-normal leading-[14.52px] text-left text-[#121212]"
                >
                  {item.icon}
                  {item.value}
                </div>
              );
            })}
          </div>
          {(impressions || engagementRate) && (
            <div className="flex justify-between">
              {impressions > 0 && (
                <div className="flex text-[#6D6D6D] font-[Inter] text-sm not-italic font-bold leading-[normal] items-center gap-1">
                  <Eye size={18} />
                  {impressions > 0 ? impressions : "-"} Impressions
                </div>
              )}
              {engagementRate && (
                <div className="flex text-[#6D6D6D] font-[Inter] text-sm not-italic font-bold leading-[normal] items-center gap-1 ">
                  <TrendingUp size={18} />
                  {engagementRate > 0 ? engagementRate?.toFixed(2) : "-"}%
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

interface HighlightMetricsCardProps {
  icon?: React.ReactNode | string;
  value: string | number;
  title: string;
  bgColor?: string;
  tooltipContent?: React.ReactNode;
}

export const HighlightMetricsCard: React.FC<HighlightMetricsCardProps> = ({
  icon,
  value,
  title,
  bgColor,
  tooltipContent,
}) => {
  tooltipContent = tooltipContent || (
    <p className="text-white">
      Engagement Rate is the percentage of followers engaging <br />
      with the users content.
    </p>
  );

  return (
    <div
      className={cn(
        "flex px-[25px] py-4 rounded-[10px] h-full   [box-shadow:0px_4px_4px_0px_rgba(0,_0,_0,_0.25)]",
        bgColor && bgColor
      )}
    >
      <div className="flex items-center gap-[12px]">
        {icon && <div className="flex items-center justify-center">{icon}</div>}
        <div className="flex flex-col">
          <span className="text-[#121212] font-[Inter] text-lg not-italic font-bold leading-[normal]">
            {value}
          </span>
          <span className="text-[#585858] cursor-pointer flex items-center gap-1 justify-center font-[Inter] text-sm not-italic font-normal leading-[normal] md:whitespace-nowrap">
            {title}
            {title === "Avg Eng." && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon size={15} />
                  </TooltipTrigger>
                  <TooltipContent className="bg-current border-none ">
                    {tooltipContent}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export const CollabFormAccordion = ({
  Header,
  ContentArray,
  defaultValue,
}: {
  Header: React.ReactNode;
  ContentArray: React.ReactNode[];
  defaultValue?: string;
}) => {
  return (
    <>
      <Accordion
        type="single"
        defaultValue={defaultValue}
        collapsible
        className="w-full"
      >
        <AccordionItem className="w-full" value={defaultValue || "my-value"}>
          <AccordionTrigger className="w-full px-[17px] py-[10px] data-[state=closed]:rounded-[10px_10px_10px_10px] data-[state=open]:rounded-[10px_10px_0px_0px] bg-[#F6F6F6]">
            {Header}
          </AccordionTrigger>
          <AccordionContent className="w-full">
            <div className="flex w-full flex-col items-start gap-[22px] shrink-0 self-stretch bg-[color:var(--Shade-White,#FFF)] px-[29px] py-[17px] rounded-[0px_0px_10px_10px] border-b-[#EEE] border-x-[#EEE] border-r border-solid border-b border-l">
              {ContentArray.map((content, index) => (
                <div className="w-full" key={index}>
                  {content}
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};

export const CampaignInfoTip = ({ content }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <InfoIcon size={12} color="#4C4F52" className="cursor-pointer" />
      </TooltipTrigger>
      <TooltipContent className="w-1/2" align="start">
        <p className="text-sm font-normal">{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

interface GaugeChartProps {
  value: number;
  max: number;
  label?: string;
  size?: number;
  thickness?: number;
  colors?: {
    background?: string;
    fill?: string;
    text?: string;
  };
}

export const BudgetGaugeChart: React.FC<GaugeChartProps> = ({
  value,
  max,
  label,
  size = 200,
  thickness = 20,
  colors = {
    background: "#e5e7eb",
    fill: "#3b82f6",
    text: "#1f2937",
  },
}) => {
  const calculateGaugeMetrics = (
    value: number,
    max: number,
    radius: number
  ) => {
    // Ensure percentage is between 0 and 100
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
    const circumference = radius * Math.PI;

    // Calculate the stroke dash array
    // For 100%, we need the full semicircle which is half the circumference
    const dashLength = (percentage * circumference) / 100;
    const strokeDasharray = `${dashLength} ${circumference}`;

    return { percentage, strokeDasharray };
  };
  const radius = (size - thickness) / 2;
  const { strokeDasharray } = calculateGaugeMetrics(value, max, radius);

  return (
    <div className="flex flex-col items-center">
      <svg
        width={size}
        height={size / 2 + thickness}
        className="overflow-visible"
      >
        {/* Background arc */}
        <path
          d={`M ${thickness / 2} ${size / 2}
              A ${radius} ${radius} 0 0 1 ${size - thickness / 2} ${size / 2}`}
          fill="none"
          stroke={colors.background}
          strokeWidth={thickness}
          strokeLinecap="round"
        />
        {/* Foreground arc */}
        <path
          d={`M ${thickness / 2} ${size / 2}
              A ${radius} ${radius} 0 0 1 ${size - thickness / 2} ${size / 2}`}
          fill="none"
          stroke={colors.fill}
          strokeWidth={thickness}
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          style={{
            transition: "stroke-dasharray 0.5s ease",
          }}
        />
        {/* Percentage text */}
        <text
          x="50%"
          y="65%"
          textAnchor="middle"
          fill={colors.text}
          className="text-[#000] font-[Inter] text-lg not-italic font-bold leading-[normal]"
        >
          {/* {Math.round(percentage)}% */}₹{value.toLocaleString()}
        </text>
        {/* Label text */}
        {label && (
          <text
            x="50%"
            y="85%"
            textAnchor="middle"
            fill={colors.text}
            className="text-[#000] text-center font-[Inter] text-[12px] not-italic font-semibold leading-[normal]"
          >
            {label}
          </text>
        )}
      </svg>
      <div className="text-center flex justify-between w-full gap-2">
        <p className="text-[#000] text-center font-[Inter] text-sm not-italic font-bold leading-[normal]">
          ₹0
        </p>
        <p className="text-[#000] text-center font-[Inter] text-sm not-italic font-bold leading-[normal]">
          ₹{max.toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export const BrandAlertDialog = ({
  trigger,
  heading,
  desc,
  onContinue,
}: {
  trigger: React.ReactNode;
  heading: string;
  desc: string;
  onContinue: (e?: any) => void;
}) => {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{heading}</AlertDialogTitle>
          <AlertDialogDescription>{desc}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onContinue}>Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const NoActivePlanAlertButton = ({
  Button,
  onClick,
}: {
  Button: React.ReactNode;
  onClick: (e?: any) => void;
  isSampleInfluencer?: boolean;
}) => {
  const authState = useAuth();
  const navigate = useNavigate();
  return (
    <>
      {authState?.loggedBrand?.activePlan ? (
        <div onClick={onClick}>{Button}</div>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>{Button}</AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                You have no current active plan
              </AlertDialogTitle>
              <AlertDialogDescription>
                Please purchase the plan to continue with campaign management
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => navigate("/brand/dashboard/pricing")}
              >
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

export const SampleCampaignAlertButton = ({
  Button,
  isSampleInfluencer = false,
}: {
  Button: React.ReactNode;
  isSampleInfluencer: boolean;
}) => {
  const { formData } = useNewCampaign();

  const shouldWorkNormally =
    !formData?.campaignInfo?.isSampleCampaign ||
    (formData?.campaignInfo?.isSampleCampaign && isSampleInfluencer);
  return (
    <>
      {shouldWorkNormally ? (
        <div>{Button}</div>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>{Button}</AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Real influencers aren’t selectable in Sample Mode
              </AlertDialogTitle>
              <AlertDialogDescription>
                To select real profiles, please create an account.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Continue with Sample</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  localStorage.removeItem("jwtToken");
                  localStorage.removeItem("userType");
                  window.open("/brand", "_self");
                }}
              >
                Create Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};
